export interface DistrictScheduleData {
  district_by_pk?: {
    weekday_window_districts?: Array<{
      weekday_window?: {
        weekday?: number | null;
        driver_window?: { timeDuration?: string | null } | null;
      } | null;
    }>;
  } | null;
}

export interface DistrictScheduleParsed {
  availableWeekdays: Set<number>;
  timeRangesByWeekday: Map<number, Array<[string, string]>>;
}

function normalizeTimeToHHmm(s: string): string {
  const trimmed = s.trim();
  const [h, m] = trimmed.split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(h)) return trimmed;
  const hours = `${h}`.padStart(2, '0');
  const minutes = Number.isNaN(m) ? '00' : `${m}`.padStart(2, '0');
  return `${hours}:${minutes}`;
}

function toIsoWeekday(weekday: number): number {
  if (weekday === 0) return 7;
  return weekday;
}

export function parseDistrictSchedule(
  data: DistrictScheduleData | undefined
): DistrictScheduleParsed | null {
  const districts = data?.district_by_pk?.weekday_window_districts;
  if (!districts?.length) return null;

  const availableWeekdays = new Set<number>();
  const timeRangesByWeekday = new Map<number, Array<[string, string]>>();

  for (const wwd of districts) {
    const rawWeekday = wwd.weekday_window?.weekday;
    const timeDuration = wwd.weekday_window?.driver_window?.timeDuration;
    if (rawWeekday == null || !timeDuration) continue;

    const weekday = toIsoWeekday(rawWeekday);
    const parts = timeDuration.replace(/"/g, '').split('-');
    if (parts.length !== 2) continue;
    const start = normalizeTimeToHHmm(parts[0]);
    const end = normalizeTimeToHHmm(parts[1]);
    if (!start || !end) continue;

    availableWeekdays.add(weekday);
    const existing = timeRangesByWeekday.get(weekday) ?? [];
    const key = `${start}-${end}`;
    if (!existing.some(([s, e]) => `${s}-${e}` === key)) {
      existing.push([start, end]);
      timeRangesByWeekday.set(weekday, existing);
    }
  }

  if (availableWeekdays.size === 0) return null;
  return { availableWeekdays, timeRangesByWeekday };
}

export function isDateAllowedBySchedule(
  date: { isoWeekday: () => number },
  schedule: DistrictScheduleParsed | null
): boolean {
  if (!schedule) return true;
  return schedule.availableWeekdays.has(date.isoWeekday());
}

export function filterTimeRangesBySchedule(
  timeRanges: Array<[string, string]>,
  weekday: number,
  schedule: DistrictScheduleParsed | null
): Array<[string, string]> {
  if (!schedule) return timeRanges;
  const isoWeekday = toIsoWeekday(weekday);
  const allowed = schedule.timeRangesByWeekday.get(isoWeekday);
  if (!allowed?.length) return [];
  const allowedSet = new Set(
    allowed.map(([s, e]) => `${normalizeTimeToHHmm(s)},${normalizeTimeToHHmm(e)}`)
  );
  return timeRanges.filter(([s, e]) =>
    allowedSet.has(`${normalizeTimeToHHmm(s)},${normalizeTimeToHHmm(e)}`)
  );
}

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map((x) => parseInt(x, 10));
  return (Number.isNaN(h) ? 0 : h) * 60 + (Number.isNaN(m) ? 0 : m);
}

function sortSlotsByTime(slots: Array<[string, string]>): Array<[string, string]> {
  return [...slots].sort(([aStart, aEnd], [bStart, bEnd]) => {
    const aStartMin = timeToMinutes(aStart);
    const bStartMin = timeToMinutes(bStart);
    if (aStartMin !== bStartMin) return aStartMin - bStartMin;
    return timeToMinutes(aEnd) - timeToMinutes(bEnd);
  });
}

export function getScheduleSlotsForWeekday(
  schedule: DistrictScheduleParsed | null,
  weekday: number,
  fallbackSlots: Array<[string, string]>
): Array<[string, string]> {
  if (!schedule) return fallbackSlots;
  const isoWeekday = toIsoWeekday(weekday);
  const slots = schedule.timeRangesByWeekday.get(isoWeekday);
  if (!slots?.length) return fallbackSlots;
  return sortSlotsByTime(slots);
}
